import React, { useEffect, useState, createElement, Component } from 'react';
import { Menu, X, MapPin, Package, BarChart3, ArrowRight, ArrowLeft, Mail, Copy, Check, Building2, Send } from 'lucide-react';
import { Button } from './ui/Button';
const PARTNER_EMAIL = 'info@samplerapp.com';
type Stage = 'slides' | 'form' | 'mockup';
interface LeadForm {
  name: string;
  company: string;
  email: string;
  interest: string;
  message: string;
}
const INTERESTS = ['Product sampling drop', 'Event activation', 'Audience & data insights', 'Brand partnership', 'Something else'];
const SLIDES = [{
  icon: MapPin,
  eyebrow: 'Psst. Brands.',
  title: 'We hide it. They hunt it.',
  body: 'Your product, dropped somewhere in the city. People come find it — on purpose.'
}, {
  icon: Package,
  eyebrow: 'The catch',
  title: 'Drop. Catch. Review.',
  body: 'They reserve. They collect. They tell you exactly what they think.'
}, {
  icon: BarChart3,
  eyebrow: 'The good stuff',
  title: 'Real mouths. Real data.',
  body: 'Who tried it. Who’d buy it. Who told a friend. All yours.'
}];
export function BrandsMenu({
  variant = 'light'
}: {
  /** "light" = dark icon for light backgrounds, "dark" = light icon. */
  variant?: 'light' | 'dark';
}) {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<Stage>('slides');
  const [slide, setSlide] = useState(0);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState<LeadForm>({
    name: '',
    company: '',
    email: '',
    interest: INTERESTS[0],
    message: ''
  });
  // Lock body scroll while the overlay is open and support Esc to close.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);
  const close = () => {
    setOpen(false);
    // Reset after the close animation so re-opening starts fresh.
    window.setTimeout(() => {
      setStage('slides');
      setSlide(0);
      setCopied(false);
    }, 250);
  };
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const formValid = form.name.trim() && form.company.trim() && emailValid;
  const subject = `Partnership inquiry — ${form.company || 'Brand'}`;
  const body = `Hi Gone Fishing team,

I'd like to explore partnering with Gone Fishing.

Name: ${form.name}
Company / Brand: ${form.company}
Email: ${form.email}
Interested in: ${form.interest}
${form.message ? `\n${form.message}\n` : ''}
Looking forward to hearing from you.`;
  const mailtoHref = `mailto:${PARTNER_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const copyEmail = () => {
    const full = `To: ${PARTNER_EMAIL}\nSubject: ${subject}\n\n${body}`;
    navigator.clipboard?.writeText(full).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }, () => {});
  };
  const iconColor = variant === 'dark' ? 'text-white' : 'text-brand-navy';
  const btnBg = variant === 'dark' ? 'bg-white/10 hover:bg-white/20' : 'bg-white/80 hover:bg-white border border-brand-navy/10';
  return <>
      {/* Hamburger trigger */}
      <button onClick={() => setOpen(true)} aria-label="Brands & organizers — learn about Gone Fishing" className={`flex items-center justify-center w-10 h-10 rounded-xl shadow-sm transition-colors ${btnBg}`} data-id="element-250" data-mp="1zua720sijnem">
        <Menu className={`w-5 h-5 ${iconColor}`} data-id="element-251" data-mp="24racc9hjgm4o" />
      </button>

      {/* Overlay */}
      {open && <div role="dialog" aria-modal="true" aria-label="For brands and organizers" className="fixed inset-0 z-[100] flex items-stretch sm:items-center sm:justify-center" data-id="element-252" data-mp="fdncyi9iy0r0">
          {/* Backdrop */}
          <button aria-label="Close" onClick={close} className="absolute inset-0 bg-brand-navy/70 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-253" data-mp="2xocjd9uzd5mq" />

          {/* Panel */}
          <div className="relative z-10 w-full sm:max-w-md bg-white sm:rounded-[2rem] shadow-2xl overflow-hidden flex flex-col animate-in slide-in-from-bottom-8 sm:zoom-in-95 duration-300 max-h-screen" data-id="element-254" data-mp="3blhn33781set">
            {/* Top bar */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0" data-id="element-255" data-mp="2jvmw723ptuo1">
              <span className="text-xs font-bold uppercase tracking-widest text-brand-blue" data-id="element-256" data-mp="2nvdl109om0px">
                For Brands & Organizers
              </span>
              <button onClick={close} aria-label="Close" className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors" data-id="element-257" data-mp="1w591t4xnrjqa">
                <X className="w-4 h-4 text-gray-600" data-id="element-258" data-mp="1sm0f1jmen2jd" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto" data-id="element-259" data-mp="1np1r5itc3rtm">
              {/* ---- SLIDES ---- */}
              {stage === 'slides' && <div className="p-7 flex flex-col min-h-[26rem]" data-id="element-260" data-mp="1jkka3reczsmj">
                  <div key={slide} className="flex-1 flex flex-col items-center text-center animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-261" data-mp="1em8zheg8pe39">
                    <div className="w-20 h-20 rounded-2xl bg-brand-blue flex items-center justify-center mb-6 shadow-lg shadow-brand-blue/30" data-id="element-262" data-mp="14wpfg5gsje6t">
                      {createElement(SLIDES[slide].icon, {
                  className: 'w-10 h-10 text-brand-yellow'
                })}
                    </div>
                    <span className="text-xs font-bold uppercase tracking-widest text-brand-blue mb-2" data-id="element-263" data-mp="qwy286y3m1tv">
                      {SLIDES[slide].eyebrow}
                    </span>
                    <h2 className="text-3xl font-display text-brand-navy leading-tight mb-3" data-id="element-264" data-mp="ca7twllyj8ys">
                      {SLIDES[slide].title}
                    </h2>
                    <p className="text-gray-500 leading-relaxed" data-id="element-265" data-mp="2d36kigdl7ge">
                      {SLIDES[slide].body}
                    </p>
                  </div>

                  {/* Progress dots */}
                  <div className="flex items-center justify-center space-x-2 my-6" data-id="element-266" data-mp="v1saep8tfqvu">
                    {SLIDES.map((_, i) => <button key={i} onClick={() => setSlide(i)} aria-label={`Go to slide ${i + 1}`} className={`h-2 rounded-full transition-all duration-300 ${i === slide ? 'w-7 bg-brand-blue' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} data-id="element-267" data-mp="zzk1xi5jtazi" data-mpi={i} />)}
                  </div>

                  {/* Nav */}
                  <div className="flex items-center gap-3" data-id="element-268" data-mp="1it60eojhvadn">
                    {slide > 0 && <Button variant="ghost" onClick={() => setSlide(s => s - 1)} className="w-auto px-5 bg-brand-light" data-id="element-269" data-mp="33ff8ede5trjp">
                        <ArrowLeft className="w-4 h-4" data-id="element-270" data-mp="3888asum5onsm" />
                      </Button>}
                    {slide < SLIDES.length - 1 ? <Button onClick={() => setSlide(s => s + 1)} className="flex-1" data-id="element-271" data-mp="2kj5v3sb3150r">
                        Next <ArrowRight className="w-4 h-4 ml-2" data-id="element-272" data-mp="2tjkkb2r6349j" />
                      </Button> : <Button variant="secondary" onClick={() => setStage('form')} className="flex-1" data-id="element-273" data-mp="2pgxokqrdkz72">
                        Partner with us <ArrowRight className="w-4 h-4 ml-2" data-id="element-274" data-mp="4qymgxx0hkcd" />
                      </Button>}
                  </div>
                </div>}

              {/* ---- FORM ---- */}
              {stage === 'form' && <div className="p-7 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-275" data-mp="287v37vetejd8">
                  <div className="w-14 h-14 rounded-2xl bg-brand-blue/10 flex items-center justify-center mb-4" data-id="element-276" data-mp="2cyv0x2vrd5gs">
                    <Building2 className="w-7 h-7 text-brand-blue" data-id="element-277" data-mp="2h187tum8r9f5" />
                  </div>
                  <h2 className="text-2xl font-display text-brand-navy mb-1" data-id="element-278" data-mp="2lz9yb0lzyb0n">
                    Tell us about you
                  </h2>
                  <p className="text-gray-500 text-sm mb-6" data-id="element-279" data-mp="2qzfjpz1ob1aa">
                    A few quick details and we'll prepare an email to our team
                    for you.
                  </p>

                  <div className="space-y-4" data-id="element-280" data-mp="wm6g2nvt69zy">
                    <Field label="Your name" placeholder="Jane Andersson" value={form.name} onChange={v => setForm({
                ...form,
                name: v
              })} data-id="element-281" data-mp="11jy95l86w25q" />
                    <Field label="Company / Brand" placeholder="NOCCO" value={form.company} onChange={v => setForm({
                ...form,
                company: v
              })} data-id="element-282" data-mp="3625wp8kftt2c" />
                    <Field label="Work email" type="email" placeholder="jane@brand.com" value={form.email} onChange={v => setForm({
                ...form,
                email: v
              })} error={form.email.length > 0 && !emailValid ? 'Enter a valid email' : undefined} data-id="element-283" data-mp="3or406x1mz39s" />
                    <div data-id="element-284" data-mp="2qx9xbwiy1ej4">
                      <label className="block text-sm font-bold text-brand-navy mb-1.5" data-id="element-285" data-mp="2m58q4p0mudzy">
                        I'm interested in
                      </label>
                      <select value={form.interest} onChange={e => setForm({
                  ...form,
                  interest: e.target.value
                })} className="w-full px-4 py-3.5 rounded-xl bg-brand-light border-2 border-transparent text-brand-navy font-medium focus:outline-none focus:border-brand-blue transition-colors" data-id="element-286" data-mp="2zv2dt40eis83">
                        {INTERESTS.map(opt => <option key={opt} value={opt} data-id="element-287" data-mp="3m4sp0hpgfiuk">
                            {opt}
                          </option>)}
                      </select>
                    </div>
                    <div data-id="element-288" data-mp="3ce7v37kq72hz">
                      <label className="block text-sm font-bold text-brand-navy mb-1.5" data-id="element-289" data-mp="3cezdtht855qu">
                        Anything else? (optional)
                      </label>
                      <textarea value={form.message} onChange={e => setForm({
                  ...form,
                  message: e.target.value
                })} rows={3} placeholder="Tell us what you have in mind…" className="w-full px-4 py-3 rounded-xl bg-brand-light border-2 border-transparent text-brand-navy font-medium placeholder:text-gray-400 focus:outline-none focus:border-brand-blue transition-colors resize-none" data-id="element-290" data-mp="33di0263ahg5b" />
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mt-6" data-id="element-291" data-mp="q71xbu2h875d">
                    <Button variant="ghost" onClick={() => setStage('slides')} className="w-auto px-5 bg-brand-light" data-id="element-292" data-mp="l8icjwzmd1el">
                      <ArrowLeft className="w-4 h-4" data-id="element-293" data-mp="1dxyws7d35bzq" />
                    </Button>
                    <Button onClick={() => setStage('mockup')} disabled={!formValid} className="flex-1" data-id="element-294" data-mp="18ymbvs5ryezs">
                      Preview email <ArrowRight className="w-4 h-4 ml-2" data-id="element-295" data-mp="z8t3t14uci0a" />
                    </Button>
                  </div>
                </div>}

              {/* ---- EMAIL MOCKUP ---- */}
              {stage === 'mockup' && <div className="p-7 animate-in fade-in slide-in-from-right-4 duration-300" data-id="element-296" data-mp="1rwf4o1mop8jg">
                  <div className="w-14 h-14 rounded-2xl bg-brand-blue/10 flex items-center justify-center mb-4" data-id="element-297" data-mp="206w730pv27bz">
                    <Mail className="w-7 h-7 text-brand-blue" data-id="element-298" data-mp="247y9trizl1ip" />
                  </div>
                  <h2 className="text-2xl font-display text-brand-navy mb-1" data-id="element-299" data-mp="295rgsms45zk1">
                    Your email is ready
                  </h2>
                  <p className="text-gray-500 text-sm mb-5" data-id="element-300" data-mp="2e39grno5l4fw">
                    Send it to our team and we'll be in touch shortly.
                  </p>

                  {/* Email mockup card */}
                  <div className="rounded-2xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-301" data-mp="2n3xvkkldf8tu">
                    <div className="bg-brand-light px-4 py-3 border-b border-gray-200 space-y-1" data-id="element-302" data-mp="2rzd5x2k66mli">
                      <Row label="To" value={PARTNER_EMAIL} data-id="element-303" data-mp="2wxy837izfgcl" />
                      <Row label="From" value={form.email} data-id="element-304" data-mp="31whs32e0mi2b" />
                      <Row label="Subject" value={subject} bold data-id="element-305" data-mp="36u1guw8cad8i" />
                    </div>
                    <pre className="px-4 py-4 text-sm text-gray-700 whitespace-pre-wrap font-sans leading-relaxed max-h-56 overflow-y-auto" data-id="element-306" data-mp="7g0q9auhnp4e">
                      {body}
                    </pre>
                  </div>

                  <div className="space-y-3 mt-6" data-id="element-307" data-mp="h6d8gwetz67d">
                    <a href={mailtoHref} className="block" data-id="element-308" data-mp="h86f4bpg1qfd">
                      <Button className="w-full" data-id="element-309" data-mp="c81z0msg6xpo">
                        <Send className="w-4 h-4 mr-2" data-id="element-310" data-mp="c9d4qs5gefjf" /> Open in email app
                      </Button>
                    </a>
                    <Button variant="secondary" onClick={copyEmail} className="w-full" data-id="element-311" data-mp="3fo8zyljhagur">
                      {copied ? <>
                          <Check className="w-4 h-4 mr-2 text-green-600" data-id="element-312" data-mp="3pkc602hgazmz" />{' '}
                          Copied
                        </> : <>
                          <Copy className="w-4 h-4 mr-2" data-id="element-313" data-mp="14v4royag0e80" /> Copy email text
                        </>}
                    </Button>
                    <button onClick={() => setStage('form')} className="w-full text-sm text-gray-500 hover:text-brand-blue transition-colors py-1" data-id="element-314" data-mp="1he0eb0ys1t5v">
                      Edit details
                    </button>
                  </div>
                </div>}
            </div>
          </div>
        </div>}
    </>;
}
function Field({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  error
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  error?: string;
}) {
  return <div data-id="element-315" data-mp="1qdx1sz9fklzc">
      <label className="block text-sm font-bold text-brand-navy mb-1.5" data-id="element-316" data-mp="1qdds25txp3we">
        {label}
      </label>
      <input type={type} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} className={`w-full px-4 py-3.5 rounded-xl bg-brand-light border-2 text-brand-navy font-medium placeholder:text-gray-400 focus:outline-none focus:border-brand-blue transition-colors ${error ? 'border-red-400' : 'border-transparent'}`} data-id="element-317" data-mp="2e2zsewm1lp5z" />
      {error && <p className="mt-1 text-xs text-red-500 font-medium" data-id="element-318" data-mp="2joqk052qjaoi">{error}</p>}
    </div>;
}
function Row({
  label,
  value,
  bold
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return <div className="flex text-xs" data-id="element-319" data-mp="29ugowfmerya3">
      <span className="w-16 shrink-0 text-gray-400 font-medium" data-id="element-320" data-mp="251x8zc090r24">{label}</span>
      <span className={`text-gray-700 break-all ${bold ? 'font-bold text-brand-navy' : ''}`} data-id="element-321" data-mp="24zufghzw0fsn">
        {value}
      </span>
    </div>;
}